import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import UserNotificationBell from "./UserNotificationBell";

const menuItems = [
  { icon: "ri-dashboard-3-line", label: "대시보드", path: "/dashboard" },
  { icon: "ri-send-plane-line", label: "문자 발송", path: "/dashboard/send" },
  { icon: "ri-history-line", label: "발송 내역", path: "/dashboard/history" },
  { icon: "ri-contacts-book-2-line", label: "주소록", path: "/dashboard/contacts" },
  { icon: "ri-calendar-schedule-line", label: "예약 발송", path: "/dashboard/schedule" },
  { icon: "ri-smartphone-line", label: "3사 테스트", path: "/dashboard/carrier-test" },
  { icon: "ri-bar-chart-2-line", label: "통계", path: "/dashboard/stats" },
  { icon: "ri-delete-bin-6-line", label: "삭제 로그", path: "/dashboard/deletion-logs" },
];

const bottomItems = [
  { icon: "ri-settings-3-line", label: "설정", path: "/dashboard/settings" },
  { icon: "ri-customer-service-2-line", label: "고객 지원", path: "/dashboard/support" },
  { icon: "ri-shield-user-line", label: "관리자 콘솔", path: "/admin" },
];

// 에이전시 전용 메뉴
const agencyItem = { icon: "ri-building-4-line", label: "에이전시", path: "/agency" };

interface Props {
  collapsed: boolean;
  onToggle: () => void;
}

interface Toast {
  id: string;
  type: "success" | "error";
  message: string;
  amount?: number;
}

export default function DashboardSidebar({ collapsed, onToggle }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isAdmin, isAgency, session } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [credit, setCredit] = useState<number | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { ...toast, id }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  useEffect(() => {
    if (!user || !session) return;
    supabase.functions.invoke("get-user-profile", {
      body: { user_id: user.id },
    })
      .then(({ data, error }) => {
        if (error) {
          console.warn("[Sidebar] credit 불러오기 실패:", error);
          return;
        }
        if (data?.error) {
          console.warn("[Sidebar] get-user-profile 오류:", data.error);
          return;
        }
        if (data?.credit !== undefined) setCredit(data.credit);
      })
      .catch((err) => console.warn("[Sidebar] credit 불러오기 실패:", err));
  }, [user, session]);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate("/");
    } catch (e) {
      console.error("[DashboardSidebar] 로그아웃 실패:", e);
    } finally {
      setSigningOut(false);
    }
  };

  const getUserInitial = () => {
    const name = user?.user_metadata?.full_name as string | undefined;
    if (name) return name.charAt(0).toUpperCase();
    return (user?.email ?? "U").charAt(0).toUpperCase();
  };

  const getUserName = () => {
    const name = user?.user_metadata?.full_name as string | undefined;
    if (name) return name;
    return user?.email?.split("@")[0] ?? "사용자";
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-[#0d0d1a] border-r border-white/[0.07] flex-col transition-all duration-300 z-40 hidden md:flex ${
        collapsed ? "w-16" : "w-60"
      }`}
    >
      {/* Logo */}
      <div className={`h-16 flex items-center border-b border-white/[0.07] shrink-0 ${collapsed ? "justify-center px-0" : "px-5 gap-2.5"}`}>
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 text-white font-bold text-sm shrink-0">
            S
          </div>
          {!collapsed && (
            <span className="text-white font-semibold text-sm whitespace-nowrap">Signal SMS</span>
          )}
        </Link>
      </div>

      {/* Toggle button */}
      <button
        onClick={onToggle}
        className={`absolute -right-3 top-[72px] w-6 h-6 flex items-center justify-center bg-[#1a1a2e] border border-white/[0.12] rounded-full text-gray-400 hover:text-white cursor-pointer transition-colors z-50`}
      >
        <i className={`${collapsed ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} text-xs`}></i>
      </button>

      {/* Notification */}
      {user?.id && (
        <div className={`${collapsed ? "px-2 py-2 flex justify-center" : "px-3 py-2"}`}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-2"} rounded-lg hover:bg-white/[0.05] px-2 py-2 transition-colors cursor-pointer`}>
            <UserNotificationBell
              userId={user.id}
              onToast={showToast}
              onClearAll={(count) => {
                showToast({
                  type: "success",
                  message: `${count}개의 알림을 삭제했습니다.`,
                });
              }}
              onCreditUpdate={setCredit}
              dropdownClassName="w-48"
            />
            {!collapsed && <span className="text-sm text-gray-400">알림</span>}
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <div className={`${collapsed ? "px-2" : "px-3"} space-y-0.5`}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 rounded-lg transition-all duration-150 cursor-pointer whitespace-nowrap group ${
                  collapsed ? "px-0 py-2.5 justify-center" : "px-3 py-2.5"
                } ${
                  isActive
                    ? "bg-gradient-to-r from-violet-600/30 to-pink-600/20 text-white border border-violet-500/20"
                    : "text-gray-400 hover:text-white hover:bg-white/[0.05]"
                }`}
                title={collapsed ? item.label : undefined}
              >
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-base ${isActive ? "text-violet-400" : "text-current"}`}></i>
                </div>
                {!collapsed && <span className="text-sm">{item.label}</span>}
                {isActive && !collapsed && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-gradient-to-r from-violet-400 to-pink-400 shrink-0"></span>
                )}
              </Link>
            );
          })}

          {/* 에이전시 메뉴 */}
          {isAgency && (
            <Link
              to={agencyItem.path}
              className={`flex items-center gap-3 rounded-lg transition-all duration-150 cursor-pointer whitespace-nowrap group ${
                collapsed ? "px-0 py-2.5 justify-center" : "px-3 py-2.5"
              } ${
                location.pathname === agencyItem.path
                  ? "bg-gradient-to-r from-amber-600/30 to-orange-600/20 text-white border border-amber-500/20"
                  : "text-gray-400 hover:text-white hover:bg-white/[0.05]"
              }`}
              title={collapsed ? agencyItem.label : undefined}
            >
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className={`${agencyItem.icon} text-base ${location.pathname === agencyItem.path ? "text-amber-400" : "text-current"}`}></i>
              </div>
              {!collapsed && (
                <span className="text-sm flex items-center gap-2">
                  {agencyItem.label}
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 whitespace-nowrap">AGENCY</span>
                </span>
              )}
              {location.pathname === agencyItem.path && !collapsed && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 shrink-0"></span>
              )}
            </Link>
          )}
        </div>

        {/* Divider */}
        <div className="mx-4 my-4 h-px bg-white/[0.06]"></div>

        <div className={`${collapsed ? "px-2" : "px-3"} space-y-0.5`}>
          {bottomItems.map((item) => {
            // 관리자 콘솔은 isAdmin인 경우만 표시
            if (item.path === "/admin" && !isAdmin) return null;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 rounded-lg transition-all duration-150 cursor-pointer whitespace-nowrap ${
                  collapsed ? "px-0 py-2.5 justify-center" : "px-3 py-2.5"
                } ${
                  item.path === "/admin"
                    ? "text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                    : isActive
                    ? "bg-white/[0.08] text-white"
                    : "text-gray-500 hover:text-white hover:bg-white/[0.05]"
                }`}
                title={collapsed ? item.label : undefined}
              >
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-base`}></i>
                </div>
                {!collapsed && <span className="text-sm">{item.label}</span>}
                {!collapsed && item.path === "/admin" && (
                  <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-400 whitespace-nowrap">ADMIN</span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Credit quick info */}
      {!collapsed && (
        <div className="mx-3 mb-2">
          <Link
            to="/dashboard/settings?tab=credit"
            className="flex items-center justify-between bg-gradient-to-r from-violet-500/10 to-pink-500/10 border border-violet-500/20 rounded-xl px-3 py-2.5 hover:border-violet-500/40 transition-all cursor-pointer group"
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-wallet-3-line text-violet-400 text-sm"></i>
              </div>
              <div>
                <p className="text-gray-500 text-[10px]">보유 잔액</p>
                <p className="text-white text-xs font-bold">
                  {credit === null ? "..." : `₩${credit.toLocaleString()}`}
                </p>
              </div>
            </div>
            <i className="ri-arrow-right-s-line text-gray-600 group-hover:text-violet-400 text-sm transition-colors"></i>
          </Link>
        </div>
      )}

      {/* User profile */}
      <div className={`border-t border-white/[0.07] p-3 shrink-0`}>
        {collapsed ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-pink-500 text-white text-xs font-semibold">
              {getUserInitial()}
            </div>
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-red-400 cursor-pointer transition-colors"
              title="로그아웃"
            >
              <i className="ri-logout-box-r-line text-sm"></i>
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-pink-500 text-white text-xs font-semibold shrink-0">
              {getUserInitial()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-medium truncate">{getUserName()}</p>
              <p className="text-gray-500 text-xs truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-red-400 cursor-pointer transition-colors shrink-0"
              title="로그아웃"
            >
              {signingOut ? (
                <i className="ri-loader-4-line animate-spin text-sm"></i>
              ) : (
                <i className="ri-logout-box-r-line text-sm"></i>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Toast notifications */}
      <div className="fixed top-20 left-4 z-[100] space-y-3 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg backdrop-blur-md transition-all animate-in slide-in-from-right duration-300 ${
              toast.type === "success"
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                : "bg-red-500/10 border-red-500/30 text-red-400"
            }`}
          >
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${
              toast.type === "success" ? "bg-emerald-500/20" : "bg-red-500/20"
            }`}>
              <i className={`${toast.type === "success" ? "ri-check-line" : "ri-close-line"} text-lg`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                {toast.type === "success" ? "알림" : "알림"}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">{toast.message}</p>
            </div>
            <button
              onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
              className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer shrink-0"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        ))}
      </div>
    </aside>
  );
}